<template>
  <div class="theme-form">
    <div class="theme-form-fields">
      <label class="mb-1" for="theme-name">Name</label>
      <input
        id="theme-name"
        v-model="name"
        class="form-control"
        type="text"
        :aria-invalid="!!nameError"
        aria-describedby="theme-name-note"
        placeholder="e.g. Canada Basketball Red"
        @keyup.enter="submit"
        @keyup.esc="$emit('cancel')"
      />
      <small
        v-if="nameError"
        id="theme-name-note"
        class="text-danger d-block"
        role="alert"
      >
        {{ nameError }}
      </small>
      <small v-else id="theme-name-note" class="text-muted d-block">
        Shown under the swatch in everyone's theme picker.
      </small>

      <label class="d-block mb-1 mt-3" for="theme-colour">Accent colour</label>
      <button
        id="theme-colour"
        type="button"
        class="btn btn-light colour-button"
        @click="openPicker"
      >
        <span class="colour-chip" :style="{ backgroundColor: color }"></span>
        <span class="colour-hex">{{ color }}</span>
        <span class="text-muted ml-2">Change</span>
      </button>
      <small class="text-muted d-block mt-1">
        Only the accent is set — borders, lines, buttons and charts. Background,
        chrome and text stay on the default colours.
      </small>

      <div class="mt-4">
        <button
          type="button"
          class="btn btn-theme"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ submitLabel }}
        </button>
        <button
          v-if="theme"
          type="button"
          class="btn btn-light ml-2"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
      </div>
    </div>

    <div class="theme-form-preview">
      <div class="preview-label mb-2">Preview</div>
      <theme-preview :theme-color="color" />
    </div>

    <color-picker-modal
      v-model="showPicker"
      modal-name="theme-colour-picker"
      hide-alpha
      :color="pickerRgba"
      @change="onPickerChange"
      @confirm="onPickerConfirm"
    />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";
import ColorPickerModal from "@/components/ColorPickerModal.vue";
import ThemePreview from "@/components/Themes/ThemePreview.vue";
import { BUILT_IN_THEMES, rgbToOpaqueHex, toRgb } from "@/utils/theme";

const MAX_NAME_LENGTH = 60;

// ZWNJ (U+200C) and ZWJ (U+200D) are left out on purpose: they carry meaning
// in Persian and join emoji sequences.
// Must stay identical to ThemeSettingsService::INVISIBLE_CHARACTERS.
const INVISIBLE_CHARACTERS = /[\u00AD\u200B\u200E\u200F\u2060\uFEFF]/g;

function normalizeName(name) {
  return (name || "")
    .replace(INVISIBLE_CHARACTERS, "")
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .trim();
}

function rgbaStringToHex(value) {
  const [red, green, blue] = value.split(",").map(Number);
  return rgbToOpaqueHex({ red, green, blue });
}

function liveAccent() {
  const live = getComputedStyle(document.documentElement)
    .getPropertyValue("--theme-color")
    .trim();
  return rgbToOpaqueHex(toRgb(live || BUILT_IN_THEMES[0].themeColor));
}

export default {
  name: "ThemeForm",
  components: {
    ColorPickerModal,
    ThemePreview,
  },
  props: {
    // The clinic theme being edited; null adds a new one.
    theme: {
      type: Object,
      default: null,
    },
  },
  emits: ["saved", "cancel"],
  data() {
    return {
      name: "",
      color: BUILT_IN_THEMES[0].themeColor,
      showPicker: false,
      colorBeforePicker: null,
      pickerConfirmed: false,
    };
  },
  computed: {
    ...mapState({
      saving: (state) => state.settings.themeSettings.saving,
      clinicThemes: (state) => state.settings.themeSettings.data.themes,
    }),
    nameError() {
      const name = this.name.trim();
      if (!name) return "";
      // Code points, not .length's UTF-16 units, to match the server's mb_strlen.
      if ([...name].length > MAX_NAME_LENGTH) {
        return `Name must be ${MAX_NAME_LENGTH} characters or fewer.`;
      }
      const candidate = normalizeName(name).toLowerCase();
      const taken = (this.clinicThemes || []).some(
        (existing) =>
          existing.id !== this.theme?.id &&
          normalizeName(existing.name).toLowerCase() === candidate
      );
      return taken ? "A theme with this name already exists." : "";
    },
    canSubmit() {
      return !!this.name.trim() && !this.nameError && !this.saving;
    },
    submitLabel() {
      if (this.theme) {
        return this.saving ? "Saving..." : "Save changes";
      }
      return this.saving ? "Adding..." : "Add Theme";
    },
    pickerRgba() {
      const { red, green, blue } = toRgb(this.color);
      return `${red}, ${green}, ${blue}, 1`;
    },
    // The parent rebuilds the theme object on every store refresh; only a different id resets the draft.
    themeId() {
      return this.theme?.id ?? null;
    },
  },
  watch: {
    themeId: {
      immediate: true,
      handler() {
        this.name = this.theme ? this.theme.name : "";
        this.color = this.theme ? this.theme.themeColor : liveAccent();
      },
    },
    showPicker(open) {
      if (open) return;
      if (!this.pickerConfirmed && this.colorBeforePicker) {
        this.color = this.colorBeforePicker;
      }
      this.pickerConfirmed = false;
      this.colorBeforePicker = null;
    },
  },
  methods: {
    ...mapActions({
      getThemeSettings: "settings/getThemeSettings",
      addTheme: "settings/addTheme",
      updateTheme: "settings/updateTheme",
    }),
    openPicker() {
      this.colorBeforePicker = this.color;
      this.pickerConfirmed = false;
      this.showPicker = true;
    },
    onPickerChange(rgba) {
      this.color = rgbaStringToHex(rgba);
    },
    onPickerConfirm(rgba) {
      this.pickerConfirmed = true;
      this.color = rgbaStringToHex(rgba);
    },
    async submit() {
      if (!this.canSubmit) return;
      const payload = { name: this.name.trim(), themeColor: this.color };
      const saved = this.theme
        ? await this.updateTheme({ id: this.theme.id, ...payload })
        : await this.addTheme(payload);
      if (!saved) {
        // Another admin may have taken the name; reloading turns the popup into the inline error.
        await this.getThemeSettings({ force: true });
        return;
      }
      Swal.fire(
        this.theme
          ? {
              title: "Theme Updated",
              text: `"${saved.name}" has been saved. Anyone already using it moves to the new colour the next time they load the app.`,
              icon: "success",
            }
          : {
              title: "Theme Added",
              text: `"${saved.name}" is now available in everyone's theme picker.`,
              icon: "success",
            }
      );
      if (!this.theme) {
        this.name = "";
      }
      this.$emit("saved", saved);
    },
  },
};
</script>

<style scoped lang="scss">
.theme-form {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.theme-form-fields {
  flex: 1 1 300px;
  min-width: 0;
}

.theme-form-preview {
  flex: 0 1 260px;
  min-width: 220px;
}

.preview-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: var(--gray);
  text-transform: uppercase;
}

.colour-button {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d2ddec;
}

.colour-chip {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid rgba(18, 38, 63, 0.2);
  margin-right: 10px;
}

.colour-hex {
  font-family: monospace;
  text-transform: uppercase;
}
</style>
